import {createContext, useContext, useRef} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createCodeownersSlice, type CodeownersSlice} from './slices/codeowners'
import {
  createComparisonInfoSlice,
  type ComparisonInfoSlice,
  type ComparisonInfoSliceOpts,
} from './slices/comparison-info'
import {createDiffFilterSlice, type DiffFilterSlice} from './slices/diff-filter'
import {createDiffSummariesSlice, type DiffSummariesSlice, type DiffSummariesSliceOpts} from './slices/diff-summaries'
import {createMarkersSlice, type MarkersSlice, type MarkersSliceOpts} from './slices/markers'
import {
  createProgressiveDiffsSlice,
  type ProgressiveDiffSliceOpts,
  type ProgressiveDiffsSlice,
} from './slices/progressive-diffs'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {getSelectedFullDiffHash, parsePathDigestWithoutLineNumbers} from '@github-ui/diffs/document-hash-helpers'
import {useWhitespacePreference} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'
import {
  createFilesCollapsedStatusSlice,
  type FilesCollapsedStatusSlice,
  type FilesCollapsedStatusSliceOpts,
} from './slices/files-collapsed-status'
import {
  buildCollapsedDiffStatusMap,
  buildDiffEntryMap,
  buildDiffSummaryMap,
  buildNormalizedMarkers,
  buildPendingReview,
} from '../helpers/diff-helpers'
import type {DiffEntry} from '@github-ui/diff-lines'
import {createDiffEntriesSlice, type DiffEntriesSlice, type DiffEntriesSliceOpts} from './slices/diff-entries'
import {diffEntriesKey} from '../page-data/loaders/use-diff-entries'
import {getQueryClient} from '@github-ui/react-core/query-client'
import {createPendingReviewSlice, type PendingReviewSlice, type PendingReviewSliceOpts} from './slices/pending-review'
import {
  createMarkerAndDiffNavigationSlice,
  type MarkerAndDiffNavigationOpts,
  type MarkerAndDiffNavigationSlice,
} from './slices/marker-and-diff-navigation'
import {createSettingsSlice, type SettingsSlice, type SettingsSliceOpts} from './slices/settings'
import {createPullRequestSlice, type PullRequestSlice, type PullRequestSliceOpts} from './slices/pull-request'
import {
  createSuggestionBatchSlice,
  type SuggestionBatchSlice,
  type SuggestionBatchSliceOpts,
} from './slices/suggestion-batch'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {
  createChangeGroupGenerationSlice,
  type ChangeGroupGenerationSlice,
  type ChangeGroupGenerationSliceOpts,
} from './slices/change-group-generation'
import {loadActiveBatchFromStorage} from '../helpers/suggestion-batch-storage'
import {createRepositorySlice, type RepositorySlice, type RepositorySliceOpts} from './slices/repository'

type InitialState = ChangeGroupGenerationSliceOpts &
  ComparisonInfoSliceOpts &
  DiffEntriesSliceOpts &
  DiffSummariesSliceOpts &
  FilesCollapsedStatusSliceOpts &
  MarkerAndDiffNavigationOpts &
  MarkersSliceOpts &
  SettingsSliceOpts &
  PendingReviewSliceOpts &
  ProgressiveDiffSliceOpts &
  PullRequestSliceOpts &
  RepositorySliceOpts &
  SuggestionBatchSliceOpts
export type State = ChangeGroupGenerationSlice &
  CodeownersSlice &
  ComparisonInfoSlice &
  DiffEntriesSlice &
  DiffFilterSlice &
  DiffSummariesSlice &
  FilesCollapsedStatusSlice &
  MarkerAndDiffNavigationSlice &
  MarkersSlice &
  SettingsSlice &
  PullRequestSlice &
  RepositorySlice &
  SuggestionBatchSlice &
  PendingReviewSlice &
  ProgressiveDiffsSlice

type PullRequestStore = ReturnType<typeof createPullRequestStore>

const PullRequestStoreContext = createContext<PullRequestStore | null>(null)

export function usePullRequestStore<T>(selector: (state: State) => T): T {
  const store = useContext(PullRequestStoreContext)
  if (!store) throw new Error('Missing PullRequestStoreProvider in the tree')
  return useStore(store, selector)
}

export const PullRequestStoreProvider = ({
  initData,
  children,
}: React.PropsWithChildren & {initData: FilesRoutePayload}) => {
  const storeRef = useRef<PullRequestStore | null>(null)
  const prxFilesLiteThrottle = useFeatureFlag('prx_files_lite_throttle')
  const prxFilesMediumThrottle = useFeatureFlag('prx_files_medium_throttle')
  const hideWhitespace = useWhitespacePreference(initData.user.viewSettings.hideWhitespace)
  const commentsPreference = initData.user.viewSettings.commentsPreference
  const diffSummaryMap = buildDiffSummaryMap(initData.diffSummaries)
  // this is also used as the initial state for all viewed files
  const collapsedFilesMap = buildCollapsedDiffStatusMap(initData.diffSummaries)
  const pendingReview = buildPendingReview(initData.viewerPendingReview)
  const initialDiffEntryMap = buildDiffEntryMap(initData.diffContents as DiffEntry[])
  const normalizedMarkers = buildNormalizedMarkers(initData.markers, initData.diffSummaries)
  const loadedBatch = loadActiveBatchFromStorage(initData.pullRequest.pathName, initData.pullRequest.comparison.headOid)

  // eslint-disable-next-line react-hooks/refs
  if (!storeRef.current) {
    populateTanstackDiffEntryCache(
      initData.comparison.selectedRange?.baseOid,
      initData.comparison.selectedRange?.headOid,
      initData.pullRequest.pathName,
      initData.diffContents as DiffEntry[],
      initData.pullRequest.comparison,
      hideWhitespace,
    )

    storeRef.current = createPullRequestStore({
      baseOid: initData.pullRequest.comparison.baseOid,
      headOid: initData.pullRequest.comparison.headOid,
      selectedCommitRange: initData.comparison.selectedRange,
      comparisonView: initData.comparison.viewing,
      changeGroupGeneration: initData.changeGroupGeneration,
      hideWhitespace,
      isSingleFileMode: initData.isSingleFileMode,
      collapsedFilesMap,
      initialSummariesViewedStatus: collapsedFilesMap,
      initialSummariesViewedCount: initData.user.viewedFilesCount,
      diffEntries: initialDiffEntryMap,
      commentsPreference,
      initialDiffSummaries: diffSummaryMap,
      initialLoadedPathDigests: new Set(initData.diffContents.map(entry => entry.pathDigest)),
      markers: normalizedMarkers,
      basePath: initData.pullRequest.pathName,
      pendingReview,
      prxFilesLiteThrottle,
      prxFilesMediumThrottle,
      pullRequest: initData.pullRequest,
      baseRepository: initData.repository,
      selectedPathDigest: parsePathDigestWithoutLineNumbers(getSelectedFullDiffHash()),
      initialActiveBatch: loadedBatch.length > 0 ? loadedBatch : undefined,
      showChangeGroupPreference: initData.user.viewSettings.showChangeGroups,
    })
  }

  // eslint-disable-next-line react-hooks/refs
  return <PullRequestStoreContext value={storeRef.current}>{children}</PullRequestStoreContext>
}

const createPullRequestStore = (initState: InitialState) => {
  return createStore<State>()(
    devtools(
      (...a) => ({
        ...createChangeGroupGenerationSlice(initState)(...a),
        ...createCodeownersSlice()(...a),
        ...createComparisonInfoSlice(initState)(...a),
        ...createDiffFilterSlice()(...a),
        ...createDiffSummariesSlice(initState)(...a),
        ...createMarkersSlice(initState)(...a),
        ...createProgressiveDiffsSlice(initState)(...a),
        ...createFilesCollapsedStatusSlice(initState)(...a),
        ...createPendingReviewSlice(initState)(...a),
        ...createDiffEntriesSlice(initState)(...a),
        ...createMarkerAndDiffNavigationSlice(initState)(...a),
        ...createSettingsSlice(initState)(...a),
        ...createPullRequestSlice(initState)(...a),
        ...createRepositorySlice(initState)(...a),
        ...createSuggestionBatchSlice(initState)(...a),
      }),
      {name: 'pull-request-store'},
    ),
  )
}

function populateTanstackDiffEntryCache(
  startOid: string | null | undefined,
  endOid: string | null | undefined,
  basePath: string,
  diffEntries: DiffEntry[],
  comparison: {
    baseOid: string
    headOid: string
  },
  hideWhitespace: boolean,
): void {
  const queryClient = getQueryClient()
  const queryKey = diffEntriesKey({
    basePath,
    effectiveRange: {
      baseOid: startOid ?? comparison.baseOid,
      headOid: endOid ?? comparison.headOid,
    },
    hideWhitespace,
  })
  queryClient.setQueryData(queryKey, diffEntries)
}

try{ PullRequestStoreContext.displayName ||= 'PullRequestStoreContext' } catch {}
try{ PullRequestStoreProvider.displayName ||= 'PullRequestStoreProvider' } catch {}